import React, {
  useRef,
  useState,
  useEffect,
  isValidElement,
  Fragment,
  ReactElement,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import { Loading, useTranslate } from "react-admin";
import { Loader } from "@googlemaps/js-api-loader";
import { GoogleMapContext } from "./context";
import { Theme, useTheme, Typography } from "@material-ui/core";
import ErrorIcon from "@material-ui/icons/Error";
import { ControlPosition, MapImperativeControls } from "./types";
import ReactDOM from "react-dom";
import { useProLogsMapTheme } from "../../common/hooks/useProLogsMapTheme";

interface MapBoundsChangedEventArgs {
  southWest?: google.maps.LatLng;
  zoom?: number;
  projection?: google.maps.Projection;
}

export type MapBoundsChangedListener = (
  payload: MapBoundsChangedEventArgs
) => void;

interface Props {
  loading?: boolean;
  error?: any;
  children?: React.ReactNode;
  criticalError?: boolean;
  controls?: { [name: string]: ReactElement };
  controlPositions?: { [name: string]: ControlPosition };
  alwaysPan?: boolean;
  defaultMapType?: "roadmap" | "terrain" | "satellite" | "hybrid";
  defaultZoomLevel?: number;
  highlightedRoute?: Array<{ lat: number; lng: number }>;
  visitedRoute?: Array<{ lat: number; lng: number }>;
  routeColor?: string;
  highlightedRouteOpacity?: number;
  gradientVisitedRoute?: boolean;
  panToHighlightedRoute?: boolean;
  panToCoordinates?: { lat?: number; lng?: number };
  onBoundsChanged?: MapBoundsChangedListener;
  zoomControl?: boolean;
  mapTypeControl?: boolean;
  scaleControl?: boolean;
  streetViewControl?: boolean;
  rotateControl?: boolean;
  fullscreenControl?: boolean;
  onMapLoaded?: (loaded: boolean) => void;
  onDragStart?: () => void;
  onZoomChanged?: (zoom?: number) => void;
  disableChildAutoPan?: boolean;
  interactionLock?: boolean;
}

interface RouteSegment {
  opacity: number;
  path: Array<{ lat: number; lng: number }>;
}

const EARTH_RADIUS_METERS = 6_371_000;
const DEFAULT_HIGHLIGHTED_ROUTE_OPACITY = 0.28;
const MIN_VISITED_ROUTE_OPACITY = 0.04;
const VISITED_ROUTE_GRADIENT_LEVELS = 12;

const toRadians = (value: number) => (value * Math.PI) / 180;

const getRoutePointDistanceMeters = (
  from: { lat: number; lng: number },
  to: { lat: number; lng: number }
) => {
  const latitudeDelta = toRadians(to.lat - from.lat);
  const longitudeDelta = toRadians(to.lng - from.lng);
  const startLatitude = toRadians(from.lat);
  const endLatitude = toRadians(to.lat);

  const haversine =
    Math.sin(latitudeDelta / 2) * Math.sin(latitudeDelta / 2) +
    Math.cos(startLatitude) *
      Math.cos(endLatitude) *
      Math.sin(longitudeDelta / 2) *
      Math.sin(longitudeDelta / 2);

  return (
    2 *
    EARTH_RADIUS_METERS *
    Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine))
  );
};

const interpolateRoutePoint = (
  from: { lat: number; lng: number },
  to: { lat: number; lng: number },
  progress: number
) => ({
  lat: from.lat + (to.lat - from.lat) * progress,
  lng: from.lng + (to.lng - from.lng) * progress,
});

const sliceRoutePathFromDistance = (
  path: Array<{ lat: number; lng: number }>,
  distances: number[],
  startDistance: number
) => {
  if (path.length < 2) {
    return path;
  }

  if (startDistance <= 0) {
    return path;
  }

  const totalDistance = distances[distances.length - 1];
  if (startDistance >= totalDistance) {
    return [path[path.length - 1]];
  }

  let sourceIndex = 1;
  while (
    sourceIndex < distances.length &&
    distances[sourceIndex] < startDistance
  ) {
    sourceIndex += 1;
  }

  if (sourceIndex >= distances.length) {
    return [path[path.length - 1]];
  }

  const previousDistance = distances[sourceIndex - 1];
  const nextDistance = distances[sourceIndex];
  const segmentDistance = nextDistance - previousDistance;
  const progress =
    segmentDistance > 0
      ? (startDistance - previousDistance) / segmentDistance
      : 0;

  return [
    interpolateRoutePoint(path[sourceIndex - 1], path[sourceIndex], progress),
    ...path.slice(sourceIndex),
  ];
};

const buildGradientRouteSegments = (
  path: Array<{ lat: number; lng: number }>
): RouteSegment[] => {
  if (path.length < 2) {
    return [];
  }

  const distances = [0];
  let totalDistance = 0;
  for (let index = 1; index < path.length; index += 1) {
    totalDistance += getRoutePointDistanceMeters(path[index - 1], path[index]);
    distances.push(totalDistance);
  }

  if (totalDistance <= 0) {
    return [];
  }

  const gradientSegments: RouteSegment[] = [
    {
      opacity: MIN_VISITED_ROUTE_OPACITY,
      path,
    },
  ];
  const overlayOpacityIncrement =
    (1 - MIN_VISITED_ROUTE_OPACITY) / (VISITED_ROUTE_GRADIENT_LEVELS - 1);

  for (
    let overlayIndex = 1;
    overlayIndex < VISITED_ROUTE_GRADIENT_LEVELS;
    overlayIndex += 1
  ) {
    const startDistance =
      (totalDistance * overlayIndex) / VISITED_ROUTE_GRADIENT_LEVELS;
    const slicedPath = sliceRoutePathFromDistance(
      path,
      distances,
      startDistance
    );
    if (slicedPath.length < 2) {
      continue;
    }

    gradientSegments.push({
      opacity: overlayOpacityIncrement,
      path: slicedPath,
    });
  }

  return gradientSegments;
};

export const GoogleMap = forwardRef<MapImperativeControls, Props>(
  (props, ref) => {
    const {
      loading,
      error,
      criticalError,
      children,
      controls,
      controlPositions,
      alwaysPan,
      defaultMapType = "roadmap",
      defaultZoomLevel = 5,
      highlightedRoute,
      visitedRoute,
      routeColor,
      highlightedRouteOpacity = DEFAULT_HIGHLIGHTED_ROUTE_OPACITY,
      gradientVisitedRoute = false,
      panToHighlightedRoute,
      panToCoordinates,
      onBoundsChanged,
      zoomControl = true,
      mapTypeControl = true,
      scaleControl = true,
      streetViewControl = false,
      rotateControl = false,
      fullscreenControl = true,
      onMapLoaded,
      onDragStart,
      onZoomChanged,
      disableChildAutoPan = false,
      interactionLock = false,
    } = props;
    const translate = useTranslate();
    const mapRef = useRef<HTMLDivElement>(null);
    const [map, setMap] = useState<google.maps.Map>();
    const googleMapStyles = useProLogsMapTheme();
    const theme: Theme = useTheme();
    const [zoomChanged, setZoomChanged] = useState<boolean>(false);
    const [savedRouteBounds, setSavedRouteBounds] = useState<
      google.maps.LatLngBoundsLiteral
    >();
    const zoomRef = useRef<number>(0);
    const initialInteractionLockRef = useRef(interactionLock);
    const renderedControlDivsRef = useRef<Record<string, HTMLDivElement>>({});
    const highlightedRouteRef = useRef<google.maps.Polyline | null>(null);
    const visitedRouteRefs = useRef<google.maps.Polyline[]>([]);
    const boundsRef = useRef<{ lat: number; lng: number }>({
      lat: 0,
      lng: 0,
    });

    useEffect(() => {
      if (!onMapLoaded) {
        return;
      }

      onMapLoaded(!!map);
    }, [map, onMapLoaded]);

    const fitBoundsToChildren = useCallback(() => {
      if (!map) {
        return;
      }

      const invalidChildren = React.Children.map(children, (child) => {
        if (!isValidElement(child)) return child;
      });

      if (invalidChildren?.length) {
        return;
      }

      // If there is only 1 marker, we can just set the map center according to it
      if (React.Children.count(children) === 1) {
        React.Children.forEach(children, (child) => {
          if (isValidElement(child)) {
            map.setCenter({ lat: child.props.lat, lng: child.props.lng });
          }
        });

        return;
      }

      const bounds = new google.maps.LatLngBounds();
      React.Children.forEach(children, (child) => {
        if (child && isValidElement(child)) {
          bounds.extend({ lat: child.props.lat, lng: child.props.lng });
        }
      });

      map.fitBounds(bounds);
      map.panToBounds(bounds);
    }, [map, children]);

    const fitBoundsToCoordinates = useCallback(
      (coordinates: Array<{ lat?: number; lng?: number }>) => {
        if (!map) {
          return;
        }

        const bounds = new google.maps.LatLngBounds();
        for (const coords of coordinates) {
          const { lat, lng } = coords;
          if (!lat || !lng) {
            continue;
          }

          bounds.extend({ lat, lng });
        }

        map.fitBounds(bounds);
        map.panToBounds(bounds);
      },
      [map]
    );

    useImperativeHandle(
      ref,
      (): MapImperativeControls => ({
        resetView() {
          setSavedRouteBounds(undefined);
          fitBoundsToChildren();
        },
        setCenter(coordinates: { lat: number; lng: number }) {
          map?.setCenter(coordinates);
        },
        setZoom(zoom: number) {
          map?.setZoom(zoom);
        },
        panToCoordinates(coordinates: Array<{ lat?: number; lng?: number }>) {
          setSavedRouteBounds(undefined);
          fitBoundsToCoordinates(coordinates);
        },
      }),
      [fitBoundsToChildren, fitBoundsToCoordinates, map]
    );

    // Effect to load the map
    useEffect(() => {
      if (!mapRef.current) {
        return;
      }

      if (loading) {
        return;
      }

      if (error && criticalError) {
        return;
      }

      const loader = new Loader({
        id: "googlemap",
        apiKey: "AIzaSyCUBrZAf7Q4zF_Sd-KKtxvvvH3YdDcwlbE",
        version: "quarterly",
        language: "en",
        region: "US",
        libraries: ["drawing"],
      });

      loader
        .load()
        .then(() => {
          const initialInteractionLock = initialInteractionLockRef.current;
          const loadedMap = new google.maps.Map(mapRef.current!, {
            center: { lat: 38, lng: -90 },
            mapTypeId: defaultMapType,
            zoom: defaultZoomLevel,
            disableDefaultUI: true,
            zoomControl: initialInteractionLock ? false : zoomControl,
            scaleControl: scaleControl,
            streetViewControl: streetViewControl,
            rotateControl: rotateControl,
            fullscreenControl: fullscreenControl,
            mapTypeControl: mapTypeControl,
            draggable: !initialInteractionLock,
            scrollwheel: !initialInteractionLock,
            disableDoubleClickZoom: initialInteractionLock,
            keyboardShortcuts: !initialInteractionLock,
            gestureHandling: initialInteractionLock ? "none" : "auto",
            mapTypeControlOptions: mapTypeControl
              ? {
                  style: google.maps.MapTypeControlStyle.DEFAULT,
                  mapTypeIds: ["roadmap", "terrain", "satellite", "hybrid"],
                }
              : undefined,
          });
          if (onBoundsChanged) {
            boundsRef.current = loadedMap.getBounds()?.getCenter().toJSON() ?? {
              lat: 0,
              lng: 0,
            };
            zoomRef.current = loadedMap.getZoom() ?? 0;
            onBoundsChanged({
              southWest: loadedMap.getBounds()?.getSouthWest(),
              zoom: loadedMap.getZoom(),
              projection: loadedMap.getProjection(),
            });
          }

          setMap(loadedMap);
          setZoomChanged(false);
        })
        .catch((e) => console.error(e));
    }, [
      loading,
      error,
      criticalError,
      defaultMapType,
      defaultZoomLevel,
      onBoundsChanged,
      fullscreenControl,
      mapTypeControl,
      rotateControl,
      scaleControl,
      streetViewControl,
      zoomControl,
    ]);

    useEffect(() => {
      if (!map) {
        return;
      }

      map.setOptions({
        draggable: !interactionLock,
        scrollwheel: !interactionLock,
        disableDoubleClickZoom: interactionLock,
        keyboardShortcuts: !interactionLock,
        gestureHandling: interactionLock ? "none" : "auto",
        zoomControl: interactionLock ? false : zoomControl,
      });
    }, [interactionLock, map, zoomControl]);

    // Effect to paint the map on theme change
    useEffect(() => {
      if (!map) {
        return;
      }

      map.setOptions({
        styles: googleMapStyles,
      });
    }, [map, googleMapStyles]);

    useEffect(() => {
      if (!map) {
        return;
      }

      if (!highlightedRouteRef.current) {
        highlightedRouteRef.current = new google.maps.Polyline();
      }
      const highlightedPolyline = highlightedRouteRef.current;
      if (!highlightedPolyline) {
        return;
      }

      const resolvedRouteColor = routeColor ?? theme.palette.primary.main;
      const visitedRouteSegments = gradientVisitedRoute
        ? buildGradientRouteSegments(visitedRoute ?? [])
        : visitedRoute?.length
          ? [{ opacity: 0.8, path: visitedRoute }]
          : [];

      highlightedPolyline.setOptions({
        clickable: false,
        strokeColor: resolvedRouteColor,
        strokeOpacity: highlightedRouteOpacity,
        strokeWeight: 10,
        zIndex: 3,
      });

      if (highlightedRoute?.length) {
        highlightedPolyline.setPath(highlightedRoute);
        highlightedPolyline.setMap(map);
      } else {
        highlightedPolyline.setMap(null);
      }

      while (visitedRouteRefs.current.length < visitedRouteSegments.length) {
        visitedRouteRefs.current.push(new google.maps.Polyline());
      }

      visitedRouteRefs.current.forEach((polyline, index) => {
        const segment = visitedRouteSegments[index];
        if (!segment) {
          polyline.setMap(null);
          return;
        }

        polyline.setOptions({
          clickable: false,
          strokeColor: resolvedRouteColor,
          strokeOpacity: segment.opacity,
          strokeWeight: 10,
          zIndex: 2,
        });
        polyline.setPath(segment.path);
        polyline.setMap(map);
      });

      for (
        let index = visitedRouteSegments.length;
        index < visitedRouteRefs.current.length;
        index += 1
      ) {
        visitedRouteRefs.current[index].setMap(null);
      }
    }, [
      gradientVisitedRoute,
      highlightedRoute,
      highlightedRouteOpacity,
      map,
      routeColor,
      theme.palette.primary.main,
      visitedRoute,
    ]);

    useEffect(() => {
      return () => {
        for (const controlDiv of Object.values(renderedControlDivsRef.current)) {
          ReactDOM.unmountComponentAtNode(controlDiv);
        }
        renderedControlDivsRef.current = {};
        highlightedRouteRef.current?.setMap(null);
        for (const polyline of visitedRouteRefs.current) {
          polyline.setMap(null);
        }
        visitedRouteRefs.current = [];
      };
    }, []);

    // Effect to subscribe to zoom changed and set the zoomChanged state
    useEffect(() => {
      if (!map) {
        return;
      }

      const zoomChangedListener = map.addListener("idle", () => {
        setZoomChanged(true);
        if (onBoundsChanged) {
          const zoom = map.getZoom() ?? 0;
          const bounds =
            map.getBounds()?.getCenter()?.toJSON() ??
            new google.maps.LatLng({ lat: 0, lng: 0 }).toJSON();
          if (
            zoomRef.current !== zoom ||
            bounds.lat !== boundsRef.current.lat ||
            bounds.lng !== boundsRef.current.lng
          ) {
            zoomRef.current = zoom;
            boundsRef.current = bounds;
            onBoundsChanged({
              southWest: map.getBounds()?.getSouthWest(),
              zoom: map.getZoom(),
              projection: map.getProjection(),
            });
          }
        }
      });

      return () => google.maps.event.removeListener(zoomChangedListener);
    });

    useEffect(() => {
      if (!map) {
        return;
      }

      const dragStartListener = onDragStart
        ? map.addListener("dragstart", onDragStart)
        : undefined;
      const zoomChangedListener = onZoomChanged
        ? map.addListener("zoom_changed", () => {
            onZoomChanged(map.getZoom());
          })
        : undefined;

      return () => {
        if (dragStartListener) {
          google.maps.event.removeListener(dragStartListener);
        }

        if (zoomChangedListener) {
          google.maps.event.removeListener(zoomChangedListener);
        }
      };
    }, [map, onDragStart, onZoomChanged]);

    // Effect to add custom map controls
    useEffect(() => {
      if (!map) {
        return;
      }

      const nextControls = controls ?? {};
      const controlNames = Object.getOwnPropertyNames(nextControls);
      const renderedControlIds = Object.getOwnPropertyNames(
        renderedControlDivsRef.current
      );

      for (const renderedControlId of renderedControlIds) {
        if (controlNames.includes(renderedControlId)) {
          continue;
        }

        const renderedControlDiv =
          renderedControlDivsRef.current[renderedControlId];
        const controlPositionsToScan = Object.values(
          google.maps.ControlPosition
        ).filter(
          (position): position is google.maps.ControlPosition =>
            typeof position === "number"
        );

        for (const controlPosition of controlPositionsToScan) {
          const controlsArray = map.controls[controlPosition];
          const controlIndex = controlsArray
            .getArray()
            .findIndex((item) => item === renderedControlDiv);

          if (controlIndex >= 0) {
            ReactDOM.unmountComponentAtNode(renderedControlDiv);
            controlsArray.removeAt(controlIndex);
            break;
          }
        }

        delete renderedControlDivsRef.current[renderedControlId];
      }

      for (const controlName of controlNames) {
        const controlPosition =
          (!!controlPositions ? controlPositions[controlName] : "TOP_CENTER") ??
          "TOP_CENTER";
        const targetPosition = google.maps.ControlPosition[controlPosition];
        let controlDiv = renderedControlDivsRef.current[controlName];
        if (!controlDiv) {
          controlDiv = document.createElement("div");
          controlDiv.id = controlName;
          map.controls[targetPosition].push(controlDiv);
          renderedControlDivsRef.current[controlName] = controlDiv;
        }

        ReactDOM.render(nextControls[controlName], controlDiv);
      }
    }, [map, controls, controlPositions]);

    // Effect to fit and pan map to bounds set by child markers
    useEffect(() => {
      if (!map) {
        return;
      }

      if (panToCoordinates && panToCoordinates.lat && panToCoordinates.lng) {
        const coordinateBounds = new google.maps.LatLngBounds();
        coordinateBounds.extend({
          lat: panToCoordinates.lat!,
          lng: panToCoordinates.lng,
        });

        map.fitBounds(coordinateBounds);
        map.panToBounds(coordinateBounds);
        map.setZoom(16);
        return;
      }

      if (zoomChanged && !alwaysPan && !panToHighlightedRoute) {
        return;
      }

      if (panToHighlightedRoute && (highlightedRoute || visitedRoute)) {
        const routeBounds = new google.maps.LatLngBounds();

        if (highlightedRoute) {
          for (const coords of highlightedRoute) {
            routeBounds.extend(coords);
          }
        }

        if (visitedRoute) {
          for (const coords of visitedRoute) {
            routeBounds.extend(coords);
          }
        }

        const mapBounds = map.getBounds();
        if (
          !!mapBounds &&
          !!savedRouteBounds &&
          !mapBounds.equals(savedRouteBounds)
        ) {
          if (visitedRoute) {
            map.setCenter(visitedRoute[visitedRoute.length - 1]);
            return;
          }
        }

        map.fitBounds(routeBounds);
        map.panToBounds(routeBounds);

        if (!savedRouteBounds) {
          setSavedRouteBounds(map.getBounds()?.toJSON());
        }

        return;
      }

      if (disableChildAutoPan) {
        return;
      }

      fitBoundsToChildren();
    }, [
      map,
      zoomChanged,
      alwaysPan,
      panToHighlightedRoute,
      highlightedRoute,
      visitedRoute,
      panToCoordinates,
      savedRouteBounds,
      fitBoundsToChildren,
      disableChildAutoPan,
    ]);

    if (loading) {
      return <Loading />;
    }

    return (
      <Fragment>
        {error && criticalError && (
          <div className="full-page-container">
            <div style={{ color: theme.palette.error.main }}>
              <ErrorIcon fontSize="small" />
              <Typography variant="body1">
                {translate(error, { _: error })}
              </Typography>
            </div>
          </div>
        )}
        {error && !criticalError && (
          <div
            className="map-container-top-bar"
            style={{
              backgroundColor: theme.palette.error.main,
              color: theme.palette.getContrastText(theme.palette.error.main),
            }}>
            {error}
          </div>
        )}
        <div
          className="map-container"
          ref={mapRef}
          style={{ display: error && criticalError ? "none" : "" }}>
          {children &&
            (isValidElement(children) ||
              React.Children.map(children, (child) =>
                isValidElement(child)
              )) && (
              <GoogleMapContext.Provider value={{ map }}>
                {children}
              </GoogleMapContext.Provider>
            )}
        </div>
      </Fragment>
    );
  }
);
